import { Box, CircularProgress, Divider } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { FlowPlaylistMediaWidget } from '@/components/flows/detail/edit-details/flow-playlist-media-widget';
import { SessionPlaylistMediaWidget } from '@/components/sessions/session-playlist-media-widget';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';
import { getObjectId } from '@/helpers/object.id.helper';
import { useGetSessionQuery } from '@/state/queries/sessions/use-get-session';
import {
  isEmbeddedDraftLayerSession,
  pickNewerSession,
  resolveSessionOriginIdFromFlowSlot,
} from '@/components/flows/detail/flow-detail.utils';
import { getSlotAssetsDetailsTitle } from '../edit-details/slot-assets-details-title';
import { flowControlInlinePerformMiddleWrapperSx } from '../flow-control-perform-expand';
import {
  FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID,
  FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID,
  isFlowControlFlowLevelMediaExpandId,
} from './flow-slot-assets-inline-ids';
import {
  buildInBetweenPlayoutSegmentId,
  parseInBetweenPlayoutSegmentId,
} from '@/helpers/flow-control-in-between-playout.helper';
import type { CueSheetRundownInBetween } from '@/helpers/group-cue-sheet-rundown.helper';
import { flowControlSlotIdsEqual } from '@/helpers/flow-control-playing-segment.helper';
import { resolveFlowControlPlaylistFlow } from '@/helpers/simulation-flow.helper';
import { hasFlowControlPlaylistVisibleContent } from '@/helpers/flow-media-playlist.helper';
import { FlowControlCueSegmentMediaList } from '@/components/flows/detail/slot-list/flow-control-cue-segment-media-list.tsx';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import type { FlowPlaylistScope } from '@/types/dtos/media/flow-playlist-scope.enum';
import type { FlowPlaylistSource } from '@/helpers/flow-media-playlist.helper';

const FLOW_CONTROL_MEDIA_VARIANT = 'flowControl' as const;

function FlowControlFlowPlaylistWidget({
  flow,
  liveReady,
  scrollableList,
  scope,
  slotId,
}: {
  flow: FlowPlaylistSource;
  liveReady: boolean;
  scrollableList: boolean;
  scope: FlowPlaylistScope;
  /** Disambiguates in-between transition/resume cue rows that share the same composite item id. */
  slotId?: string;
}) {
  return (
    <FlowPlaylistMediaWidget
      flow={flow}
      scope={scope}
      slotId={slotId}
      liveReady={liveReady}
      enabled
      enablePlayerSocket
      displayVariant={FLOW_CONTROL_MEDIA_VARIANT}
      hideMediaSettingsSwitches
      scrollableList={scrollableList}
    />
  );
}

/**
 * Control tab: media-only panel under a clicked slot row (draft PLAN data).
 */
export function FlowControlSlotMediaBelowRow({
  slot,
  activeSlotId,
  show,
  slotTitle,
  scrollableList = false,
  trailingInBetween,
}: {
  slot: FlowSlotDto | null;
  activeSlotId: string | null;
  show: boolean;
  slotTitle?: string;
  /** When true, media list scrolls inside the panel (overlay lower half). */
  scrollableList?: boolean;
  /** Transition/resume playlist after this section's session/break media. */
  trailingInBetween?: CueSheetRundownInBetween;
}) {
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowFromStore = useFlowStore((s) => s.flow);
  const flow = useMemo(() => resolveFlowControlPlaylistFlow(flowPlan ?? flowFromStore), [flowPlan, flowFromStore]);
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const eventSessionsOrganize = useEventStore((s) => s.eventSessionsOrganize);

  const sessionOriginId = useMemo(
    () =>
      slot
        ? resolveSessionOriginIdFromFlowSlot(slot, {
            eventSessionsPlan,
            eventSessionsOrganize,
            flowPlan,
          })
        : undefined,
    [slot, eventSessionsPlan, eventSessionsOrganize, flowPlan],
  );

  const planSessionFromStore = useMemo(() => {
    if (!sessionOriginId) return undefined;
    return eventSessionsPlan?.find((s) => {
      const sOriginId = s.origin ? getObjectId(s.origin) : s.id;
      return sOriginId === sessionOriginId;
    });
  }, [sessionOriginId, eventSessionsPlan]);

  const { data: sessionFromBackendPlan } = useGetSessionQuery(sessionOriginId, 'PLAN');

  const sessionPlan = useMemo(
    () => pickNewerSession(planSessionFromStore, sessionFromBackendPlan),
    [planSessionFromStore, sessionFromBackendPlan],
  );

  const slotSession = useMemo((): SessionDto | undefined => {
    if (!slot || slot.contentType !== 'Session' || !slot.content || typeof slot.content !== 'object') {
      return undefined;
    }
    const embedded = slot.content as SessionDto;
    if (embedded.mode === 'PLAN' || isEmbeddedDraftLayerSession(embedded.mode)) {
      return embedded;
    }
    return undefined;
  }, [slot]);

  const session = sessionPlan ?? slotSession;
  const [stickySlotId, setStickySlotId] = useState<string | null>(activeSlotId);
  const [stickySession, setStickySession] = useState<SessionDto | undefined>(undefined);

  if (activeSlotId !== stickySlotId) {
    setStickySlotId(activeSlotId);
    setStickySession(undefined);
  }
  if (session !== undefined && session !== stickySession) {
    setStickySession(session);
  }

  const sessionForWidget = session ?? stickySession;

  const resolvedSlotId = slot?.id ?? activeSlotId;
  const inBetweenPlayout = activeSlotId ? parseInBetweenPlayoutSegmentId(activeSlotId) : null;
  const isFlowLevelExpand = activeSlotId ? isFlowControlFlowLevelMediaExpandId(activeSlotId) : false;
  if (!show || !flow) return null;
  if (slot && !inBetweenPlayout) {
    if (!activeSlotId || !flowControlSlotIdsEqual(activeSlotId, slot.id)) return null;
  } else if (!activeSlotId) {
    return null;
  } else if (!isFlowLevelExpand && !flowControlSlotIdsEqual(activeSlotId, resolvedSlotId)) {
    return null;
  }

  const isPreFlowPlayout = activeSlotId === FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID;
  const isAfterFlowPlayout = activeSlotId === FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID;
  const isFlowStart = activeSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID;
  const isFlowEnd = activeSlotId === FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID;
  const liveReady = flow.liveContext?.isRunning ?? false;
  const flowParentId = flow.origin ? getObjectId(flow.origin) : flow.id;

  const commonSx: SxProps<Theme> = {
    ...flowControlInlinePerformMiddleWrapperSx,
    mx: 0,
    bgcolor: 'background.paper',
    ...(scrollableList
      ? {
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }
      : {}),
  };

  const flowPlaylistWidget = (scope: FlowPlaylistScope, playlistSlotId?: string) => (
    <Box sx={scrollableList ? { flex: 1, minHeight: 0, overflow: 'auto' } : undefined}>
      <FlowControlFlowPlaylistWidget
        flow={flow}
        liveReady={liveReady}
        scrollableList={scrollableList}
        scope={scope}
        slotId={playlistSlotId}
      />
    </Box>
  );

  const renderContent = () => {
    if (isPreFlowPlayout) {
      return flowPlaylistWidget('pre_flow');
    }

    if (inBetweenPlayout) {
      return flowPlaylistWidget(inBetweenPlayout.segmentKind, inBetweenPlayout.slotId);
    }

    if (isFlowStart) {
      return flowPlaylistWidget('flow_intro');
    }

    if (isFlowEnd) {
      return flowPlaylistWidget('flow_outro');
    }

    if (isAfterFlowPlayout) {
      return flowPlaylistWidget('after_flow');
    }

    if (!slot) return null;

    if (slot.contentType === 'Session') {
      if (!sessionForWidget) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        );
      }
      return (
        <SessionPlaylistMediaWidget
          session={sessionForWidget}
          context="Session"
          contextParentId={flowParentId}
          contextParentType="Flow"
          flowSlotId={slot.id}
          enablePlayerSocket
          enabled
          displayVariant={FLOW_CONTROL_MEDIA_VARIANT}
          hideMediaSettingsSwitches
          scrollableList={scrollableList}
        />
      );
    }

    if (slot.contentType === 'Break') {
      const title = slotTitle ?? getSlotAssetsDetailsTitle(slot);
      const hasPlaylistContent = hasFlowControlPlaylistVisibleContent(flow, 'slot', slot.id);
      if (hasPlaylistContent) {
        return (
          <FlowPlaylistMediaWidget
            flow={flow}
            scope="slot"
            slotId={slot.id}
            slotTitle={title}
            liveReady={liveReady}
            enabled
            enablePlayerSocket
            displayVariant={FLOW_CONTROL_MEDIA_VARIANT}
            hideMediaSettingsSwitches
            scrollableList={scrollableList}
          />
        );
      }
      return <FlowControlCueSegmentMediaList flow={flow} slot={slot} segmentKind="break" />;
    }

    return null;
  };

  const trailingPlaylist = trailingInBetween
    ? flowPlaylistWidget(trailingInBetween.segmentKind, trailingInBetween.slotId)
    : null;
  const trailingExpandId = trailingInBetween
    ? buildInBetweenPlayoutSegmentId(trailingInBetween.segmentKind, trailingInBetween.slotId)
    : undefined;

  return (
    <Box sx={commonSx} data-slot-inline-details data-slot-id={activeSlotId}>
      {renderContent()}
      {trailingPlaylist && trailingExpandId ? (
        <Box data-slot-id={trailingExpandId}>
          <Divider sx={{ my: 1 }} />
          {trailingPlaylist}
        </Box>
      ) : null}
    </Box>
  );
}
